import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Copy, MapPin, X } from "lucide-react";
import { toast } from "react-hot-toast";
import api, { switchLocation } from "../services/api";
import { getTokenInfo } from "../utils/auth";

type Location = { _id: string; hotelName: string };

/**
 * Menu Items header for a location admin: one tab per location (the menu
 * shown is always the active location's), plus "Copy menu" to clone the
 * active location's menu into other locations.
 */
const LocationMenuTabs: React.FC = () => {
  const tokenInfo = getTokenInfo();
  const activeId: string = tokenInfo?.restaurantId || "";
  const isLocationAdmin = tokenInfo?.role === "location_admin";

  const [locations, setLocations] = useState<Location[]>([]);
  const [switching, setSwitching] = useState("");
  const [showClone, setShowClone] = useState(false);
  const [targets, setTargets] = useState<string[]>([]);
  const [cloning, setCloning] = useState(false);

  useEffect(() => {
    if (!isLocationAdmin) return;
    api
      .get("/admin/getHotelInformation")
      .then((res) => setLocations(res.data?.data || []))
      .catch(() => setLocations([]));
  }, [isLocationAdmin]);

  if (!isLocationAdmin || !locations.length) return null;

  const active = locations.find((l) => l._id === activeId);
  const others = locations.filter((l) => l._id !== activeId);

  const openLocation = async (id: string) => {
    if (id === activeId || switching) return;
    setSwitching(id);
    try {
      const res = await switchLocation(id);
      if (res.data?.token) {
        Cookies.set("token", res.data.token);
        // Every menu request is scoped to the active location.
        window.location.reload();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Couldn't open that location");
      setSwitching("");
    }
  };

  const clone = async () => {
    if (!targets.length) return;
    setCloning(true);
    try {
      const res = await api.post("/menu-import/clone", {
        fromRestaurantId: activeId,
        toRestaurantIds: targets,
      });
      const results: { createdItems: number; skippedItems: number }[] = res.data?.data || [];
      const created = results.reduce((n, r) => n + r.createdItems, 0);
      const skipped = results.reduce((n, r) => n + r.skippedItems, 0);
      toast.success(
        `Copied ${created} items to ${targets.length} location${targets.length > 1 ? "s" : ""}` +
          (skipped ? ` (${skipped} already there)` : ""),
      );
      setShowClone(false);
      setTargets([]);
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Couldn't copy the menu");
    } finally {
      setCloning(false);
    }
  };

  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-sm text-gray-500">
          <MapPin size={14} /> Location:
        </span>
        {locations.map((loc) => (
          <button
            key={loc._id}
            type="button"
            onClick={() => openLocation(loc._id)}
            disabled={!!switching}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              loc._id === activeId
                ? "bg-blue-600 text-white shadow-sm"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            } disabled:opacity-60`}
          >
            {switching === loc._id ? "Opening…" : loc.hotelName}
          </button>
        ))}
        {others.length > 0 && (
          <button
            type="button"
            onClick={() => setShowClone(true)}
            className="ml-auto flex items-center gap-1.5 rounded-md border border-blue-600 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50"
          >
            <Copy size={14} /> Copy menu to other locations
          </button>
        )}
      </div>
      {active && (
        <p className="m-0 mt-2 text-xs text-gray-500">
          Showing the menu of <b>{active.hotelName}</b>. Changes here apply to this location only.
        </p>
      )}

      {showClone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={cloning ? undefined : () => setShowClone(false)} />
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="m-0 text-lg font-bold text-gray-800">Copy menu</h2>
              <button type="button" onClick={() => setShowClone(false)} disabled={cloning} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <p className="m-0 mb-4 text-sm text-gray-600">
              Copy all categories and items from <b>{active?.hotelName}</b> to:
            </p>
            <div className="mb-4 space-y-2">
              {others.map((loc) => (
                <label key={loc._id} className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-blue-600"
                    checked={targets.includes(loc._id)}
                    onChange={(e) =>
                      setTargets((prev) =>
                        e.target.checked ? [...prev, loc._id] : prev.filter((id) => id !== loc._id),
                      )
                    }
                  />
                  <span className="text-sm font-medium text-gray-800">{loc.hotelName}</span>
                </label>
              ))}
            </div>
            <p className="m-0 mb-4 text-xs text-gray-500">
              Items that already exist in a location (same name, same category) are skipped. Each
              location's copy can then be edited on its own.
            </p>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowClone(false)} disabled={cloning} className="rounded-md bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300">
                Cancel
              </button>
              <button
                type="button"
                onClick={clone}
                disabled={cloning || !targets.length}
                className="rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {cloning ? "Copying…" : "Copy menu"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationMenuTabs;
